import { SAGA, SIDE, SIDES, type SideBattle, type SideId, type SideSaga } from '@somnhold/content';
import { groupLevel, sideAct, sideBlocked, sideChoices, sideFlags, sideHunt, sideLines, sideOf, sideOpensAt, sideReplayAct, sideReplayPays, type CampaignState } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { linesScene } from './cellars.ts';
import { portrait } from './saga-panel.ts';

/**
 * The sagas beside the Turtle's (docs/SAGAS.md), in the chronicle: a card per saga with its question, its
 * difficulty, the chapter it is at and the way to play it; its scenes full screen; the clue board.
 */

export interface SideActions {
  /** Plays the next fight: alone, or (online, `together`) live with friends who come along. */
  play: (id: SideId, together?: boolean) => void;
  /** Opens the waiting scene. */
  scene: (id: SideId) => void;
  /** Opens the saga's clue board. */
  board: (id: SideId) => void;
  /** Plays a finished chapter's last fight again (La Orilla 5), alone or (online) with friends live. */
  replay?: (id: SideId, chapter: number, together: boolean) => void;
  /** Rooms with friends can be opened (online): the next fight and a finished chapter's, played again. */
  together?: boolean;
  /** Who is looking, and the time (for the vote). */
  me: string;
  now: () => number;
}

const nameOf = (s: CampaignState, pid: string): string => s.players.find((p) => p.id === pid)?.displayName ?? '—';

const card = (s: CampaignState, def: SideSaga, a: SideActions): HTMLElement => {
  const st = sideOf(s, def.id);
  const block = sideBlocked(s, def.id);
  const ch = def.chapters[st.chapter];
  const act = sideAct(s, def.id);
  const k = `side.${def.id}`;
  const now = h('div.side-now');
  if (block === 'over') now.append(h('p', {}, t(`${k}.over`)));
  else if (block === 'soon') now.append(h('p.saga-lock', {}, h('span', { html: ICONS.hourglass }), t('side.soon')));
  else if (block === 'level') now.append(h('p.saga-lock', {}, h('span', { html: ICONS.lock }), t('saga.blocked.level', { n: ch?.minGroup ?? 1, g: groupLevel(s, s.clock) })));
  else if (block === 'wait') now.append(h('p.saga-lock', {}, h('span', { html: ICONS.hourglass }), t('side.wait', { h: Math.max(1, Math.ceil((sideOpensAt(s, def.id) - a.now()) / 3_600_000)) })));
  else if (block === 'campaignLost') now.append(h('p.saga-lock', {}, t('saga.blocked.campaignLost')));
  else if (act?.kind === 'scene' && st.vote && ch) {
    // The group's vote: who voted what, who is missing, when it closes.
    const v = st.vote;
    const all = s.players.filter((p) => !p.left);
    const mine = v.votes.find((x) => x[0] === a.me)?.[1];
    const hours = Math.max(0, Math.ceil((v.at + SAGA.voteMs - a.now()) / 3_600_000));
    now.append(
      h('p', {}, h('b', {}, t(`side.${def.id}.${ch.id}.${act.id}.vote`))),
      h(
        'ul.saga-tally',
        {},
        ...sideChoices(s, def.id, act).map((c) => {
          const who = v.votes.filter((x) => x[1] === c.id).map((x) => x[0]);
          return h(`li${who.length ? '.on' : ''}`, {}, h('b', {}, String(who.length)), h('span', {}, t(`side.${def.id}.${ch.id}.${act.id}.c.${c.id}`)), who.length ? h('span.vote-who', {}, ...who.map((p) => h(`span.vote-chip${p === a.me ? '.me' : ''}`, {}, p === a.me ? t('saga.vote.you') : nameOf(s, p)))) : '');
        }),
      ),
      h('p.small', {}, `${t('saga.vote.count', { n: v.votes.length, of: all.length })} · ${t('side.voteCloses', { h: hours })}`),
      h('button.btn', { 'data-test': `side-vote-${def.id}`, onclick: () => a.scene(def.id) }, mine ? t('side.voteChange') : t('saga.vote.cast')),
    );
  } else if (act?.kind === 'scene') {
    const who = act.lines.find((l) => l.who !== 'narrator')?.who;
    const name = t(`npc.${who ?? def.face}`);
    now.append(h('p', {}, t('side.waiting', { who: name })), h('button.btn', { 'data-test': `side-scene-${def.id}`, onclick: () => a.scene(def.id) }, t('side.openScene', { who: name })));
  } else if (act?.kind === 'battle' && ch) {
    now.append(
      h('p.saga-goal', {}, h('b', {}, t(`${k}.${ch.id}.${act.id}.title`)), h('span', {}, t(`${k}.${ch.id}.${act.id}.${goalOf(s, def.id, act)}`))),
      act.hunt ? huntNow(s, a.now()) : '',
      h(
        'div.saga-go',
        {},
        h('button.btn', { 'data-test': `side-play-${def.id}`, onclick: () => a.play(def.id) }, t(act.hunt ? 'side.hunt.go' : 'saga.play')),
        a.together ? h('button.btn', { 'data-test': `side-together-${def.id}`, onclick: () => a.play(def.id, true) }, t('saga.together')) : '',
      ),
      // Media and up are made for two or more: said before going in alone.
      def.difficulty !== 'light' ? h('p.small', {}, t('side.forMore')) : '',
      h('p.small', {}, t(act.hunt ? 'side.hunt.retry' : 'side.retry')),
    );
  }
  // A finished chapter that pays a unique piece (La Orilla 5) can be played again by whoever still lacks it (§1).
  const again = def.chapters.flatMap((c, i) => (i < st.chapter && sideReplayAct(def.id, i) >= 0 ? [i] : []));
  for (const i of again) {
    const c = def.chapters[i];
    if (!c || !a.replay) continue;
    const mine = sideReplayPays(s, a.me, def.id, i);
    // A plate of its own (the last fight of a saga, again): its title large, what it pays, and its buttons lit when it
    // pays this hero something (the amulet, the dragon's seal).
    const btn = mine ? 'button.btn' : 'button.btn.ghost';
    now.append(
      h(
        `div.side-replay${mine ? '.pays' : ''}`,
        { 'data-test': `side-replay-${def.id}` },
        h('span.side-replay-ico', { html: ICONS.sparkle }),
        h(
          'div.side-replay-body',
          {},
          h('b.side-replay-title', {}, t('side.replay.title', { title: t(`${k}.${c.id}.title`) })),
          h('span.small', {}, t(mine ? 'side.replay.yours' : 'side.replay.legend')),
          h(
            'div.saga-go',
            {},
            h(btn, { 'data-test': `side-replay-play-${def.id}`, onclick: () => a.replay?.(def.id, i, false) }, t('saga.play')),
            a.together ? h(btn, { 'data-test': `side-replay-together-${def.id}`, onclick: () => a.replay?.(def.id, i, true) }, t('saga.together')) : '',
          ),
        ),
      ),
    );
  }
  const clues = st.clues.length;
  // Only a saga with something to find keeps a board.
  const board = def.chapters.some((c) => c.acts.some((x) => x.kind === 'battle' && (x.clues?.length || x.given?.length || x.winClues?.length)));
  return h(
    'div.side-card',
    { 'data-test': `side-${def.id}` },
    h(
      'header.side-head',
      {},
      portrait(def.face),
      h('div', {}, h('b', {}, t(`${k}.title`)), h('span.small', {}, `${t(`side.diff.${def.difficulty}`)} · ${ch ? t('side.chapter', { n: st.chapter + 1, of: def.chapters.length, title: t(`${k}.${ch.id}.title`) }) : t(`${k}.over`)}`)),
    ),
    h('p.side-question', {}, t(`${k}.question`)),
    now,
    board && (clues || st.chapter > 0) ? h('button.saga-rerun.side-board-btn', { 'data-test': `side-board-${def.id}`, onclick: () => a.board(def.id) }, h('b', {}, h('span', { html: ICONS.scroll }), ' ', t('side.board.title')), h('span.small', {}, t('side.board.count', { n: clues }))) : '',
  );
};

/**
 * The hunt between outings: how much life the Old Uro has left (healed to now), where its tracks lead today, the traps
 * waiting for it, and who wounded it how much.
 */
const huntNow = (s: CampaignState, now: number): HTMLElement => {
  const hu = sideHunt(s, now);
  if (!hu) return h('div.side-hunt', { 'data-test': 'side-hunt' }, h('p.small', {}, t('side.hunt.none')));
  const pct = Math.ceil((100 * hu.hp) / Math.max(1, hu.max));
  const wounds = [...hu.wounds].sort((x, y) => y[1] - x[1]).slice(0, 4);
  return h(
    'div.side-hunt',
    { 'data-test': 'side-hunt' },
    h('div.row', {}, h('span.label', {}, t('side.hunt.life')), h('b', {}, `${pct} %`)),
    h('div.bar.boss', {}, h('i', { style: `width:${pct}%` })),
    h('p.small', {}, [t(`side.hunt.place.${hu.place}`), hu.traps.length ? t('side.huntTraps', { n: hu.traps.length }) : '', t('side.hunt.heals', { n: Math.round(SIDE.hunt.healBp / 100) })].filter(Boolean).join(' · ')),
    wounds.length ? h('p.small', {}, t('side.hunt.wounds', { list: wounds.map(([pid, d]) => `${nameOf(s, pid)} ${Math.max(1, Math.round((100 * d) / hu.max))} %`).join(', ') })) : '',
  );
};

/** The side sagas' section: shown once the group reaches the first chapter of one (or has begun it). */
export const sidesSection = (svc: { state: CampaignState }, a: SideActions): HTMLElement | null => {
  const s = svc.state;
  // By level (El pozo, first to open, is last in the list for the fights' seeds).
  const shown = [...SIDES].sort((a, b) => (a.chapters[0]?.minGroup ?? 0) - (b.chapters[0]?.minGroup ?? 0)).filter((d) => {
    const st = sideOf(s, d.id);
    return st.chapter > 0 || st.act > 0 || groupLevel(s, s.clock) >= (d.chapters[0]?.minGroup ?? 99);
  });
  if (!shown.length) return null;
  return h('div.sides', { 'data-test': 'sides' }, h('span.label', {}, t('side.title')), ...shown.map((d) => card(s, d, a)));
};

/** Which goal line a fight shows: Ragna's and Bruni's depend on what the group chose, or on their having fallen elsewhere. */
const goalOf = (s: CampaignState, id: SideId, act: SideBattle): string => {
  if (act.flight || act.breath || act.sunken) return sideFlags(s, id).includes('orilla.corazon') ? 'goalHeart' : 'goal';
  if (act.rising) {
    const flags = sideFlags(s, id);
    return flags.includes('rails.felled') ? 'goalFelled' : flags.includes('rails.open') ? 'goalOpen' : 'goal';
  }
  if (!act.ragna) return 'goal';
  const flags = sideFlags(s, id);
  return flags.includes('horn.felled') ? 'goalFelled' : flags.includes('horn.call') ? 'goalCall' : 'goal';
};

/**
 * A side saga's waiting scene, full screen; its last line moves the saga on (`go`), or, for a decision, puts each
 * choice to the vote (`choose`, with the board one tap away), or steps aside (`later`).
 */
export const sideScene = (s: CampaignState, id: SideId, go: () => void, later: () => void, choose?: (choice: string) => void, board?: () => void, atEnd = false): HTMLElement | null => {
  const def = SIDES.find((d) => d.id === id);
  const st = sideOf(s, id);
  const ch = def?.chapters[st.chapter];
  const act = sideAct(s, id);
  if (!def || !ch || act?.kind !== 'scene') return null;
  const k = `side.${id}.${ch.id}.${act.id}`;
  const lines = sideLines(s, id, act).map((l) => ({ who: l.who, key: `${k}.${l.id}` }));
  const later_ = { label: t('saga.later'), test: 'side-later', ghost: true, run: later };
  const choices = sideChoices(s, id, act);
  if (choices.length && choose) {
    const acts = choices.map((c) => ({ label: t(`${k}.c.${c.id}`), test: `side-choice-${c.id}`, run: () => choose(c.id) }));
    return linesScene(lines, [...acts, ...(board ? [{ label: t('side.board.title'), test: 'side-board-from-scene', ghost: true, run: board }] : []), later_], {}, t(`side.${id}.title`), atEnd ? lines.length - 1 : 0);
  }
  const last = act.id === ch.acts[ch.acts.length - 1]?.id;
  return linesScene(lines, [{ label: last ? t('side.closeChapter') : t('saga.next'), test: 'side-go', run: go }, later_], {}, t(`side.${id}.title`));
};

/** The clue board: every clue the group found, one line each, who found it and when; what is still missing, as blanks. */
export const clueBoard = (s: CampaignState, id: SideId, now: number): HTMLElement => {
  const st = sideOf(s, id);
  const def = SIDES.find((d) => d.id === id);
  // Only what the chapters played so far could have left (the ones to come stay off the board).
  const all = (def?.chapters ?? []).slice(0, st.chapter + 1).flatMap((c) => c.acts.flatMap((a) => (a.kind === 'battle' ? [...(a.clues ?? []), ...(a.given ?? []), ...(a.winClues ?? [])] : [])));
  const ago = (at: number): string => {
    const hrs = Math.floor((now - at) / 3_600_000);
    return hrs < 1 ? t('side.board.justNow') : hrs < 48 ? t('side.board.hours', { n: hrs }) : t('side.board.days', { n: Math.floor(hrs / 24) });
  };
  return h(
    'div.clue-board',
    { 'data-test': 'clue-board' },
    h('p.small', {}, t('side.board.hint')),
    h(
      'ul.clue-list',
      {},
      ...st.clues.map((c) => h('li.clue', {}, h('span.clue-pin'), h('div', {}, h('p', {}, t(`clue.${c.id}`)), h('span.small', {}, t('side.board.by', { who: nameOf(s, c.pid), when: ago(c.at) }))))),
      ...all.filter((c) => !st.clues.some((x) => x.id === c)).map(() => h('li.clue.missing', {}, h('span.clue-pin'), h('div', {}, h('p', {}, t('side.board.missing'))))),
    ),
  );
};

/** How a side saga's fight ended, and the clues it found. */
export const sideResult = (s: CampaignState, id: SideId, won: boolean, title: string, found: string[], chapterDone: boolean, onBack: (el: HTMLElement) => void, why?: string, saved?: string, outing = false): HTMLElement => {
  const def = SIDES.find((d) => d.id === id) as SideSaga;
  // What waits after a won fight: a scene (the chapter's closing one, or the next) or another fight.
  const sceneNext = sideAct(s, id)?.kind === 'scene';
  const el: HTMLElement = h(
    'div.modal-back.saga-end',
    { 'data-test': 'side-result' },
    h(
      'div.modal',
      { role: 'dialog', 'aria-modal': 'true' },
      h('header', {}, h('h2', {}, won ? t('saga.result.won') : outing ? t('side.result.outing') : t('saga.result.lost')), h('p', {}, title)),
      h(
        'div.body',
        {},
        why ? h('p', {}, t(why)) : '',
        saved ? h('p', {}, h('b', {}, saved)) : '',
        // An outing after the Uro is not a fight lost: the next one picks up from here.
        outing ? '' : h('p', {}, won ? t(sceneNext ? 'side.result.sceneNext' : 'saga.result.onward') : t('side.result.retry')),
        found.length ? h('div.side-found', {}, h('span.label', {}, t('side.result.found', { n: found.length })), ...found.map((c) => h('p.clue', {}, h('span.clue-pin'), t(`clue.${c}`)))) : '',
        chapterDone ? h('p.small', {}, t('side.result.chapterDone', { title: t(`side.${id}.title`), diff: t(`side.diff.${def.difficulty}`) })) : '',
      ),
      h('footer', {}, h('button.btn', { 'data-test': 'side-back', onclick: () => onBack(el) }, won ? t('saga.result.continue') : t('saga.result.back'))),
    ),
  );
  return el;
};

/** A side saga's chapter closed (by its last scene): what it gave. */
export const sideDone = (id: SideId, chapter: number, onBack: (el: HTMLElement) => void): HTMLElement => {
  const def = SIDES.find((d) => d.id === id) as SideSaga;
  const r = SIDE.reward[def.difficulty];
  const el: HTMLElement = h(
    'div.modal-back.saga-end',
    { 'data-test': 'side-done' },
    h(
      'div.modal',
      { role: 'dialog', 'aria-modal': 'true' },
      h('header', {}, h('h2', {}, t('side.done.title')), h('p', {}, t(`side.${id}.${def.chapters[chapter]?.id}.title`))),
      h(
        'div.body',
        {},
        h('p', {}, t('side.done.body', { title: t(`side.${id}.title`), n: chapter + 1, xp: SIDE.xp })),
        h('span.saga-res', {}, ...(['wood', 'stone', 'crystal', 'dust'] as const).map((k) => h('span.cost-chip', {}, h('span', { html: ICONS[k] }), String(r[k])))),
      ),
      h('footer', {}, h('button.btn', { 'data-test': 'side-done-back', onclick: () => onBack(el) }, t('saga.result.back'))),
    ),
  );
  return el;
};
